import { createFileRoute, redirect } from "@tanstack/react-router";
import { subWeeks } from "date-fns";
import type { z } from "zod";
import { m } from "#/paraglide/messages";
import { useSearchParcels } from "#/lib/api/parcel";
import { useMarket } from "#/contexts/MarketContext";
import { ParcelSearchSchema } from "#/lib/schemas/parcel-search";
import { ParcelsPage } from "#/components/pages/parcels/parcels-page";
import { toISODateString } from "#/utils/date";

type ParcelFilter = z.infer<typeof ParcelSearchSchema>["filters"][number];

const buildDefaultArrivalDateFilter = (): ParcelFilter => ({
  id: "arrivalDate",
  value: toISODateString(subWeeks(new Date(), 1)),
  variant: "date",
  operator: "gte",
  filterId: "default-arrival-date",
});

export const Route = createFileRoute("/$tenantCode/$market/parcels")({
  validateSearch: ParcelSearchSchema,
  beforeLoad: ({ search, params }) => {
    if (search.filtersInitialized !== true) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({
        to: "/$tenantCode/$market/parcels",
        params,
        search: {
          ...search,
          filtersInitialized: true,
          filters:
            search.filters.length === 0
              ? [buildDefaultArrivalDateFilter()]
              : search.filters,
        },
      });
    }
    return { getTitle: () => m["resources.parcel.name"]() };
  },
  component: ParcelsRoute,
});

function ParcelsRoute() {
  const market = useMarket();
  const { page, perPage, sort, filters } = Route.useSearch();

  const { data, isLoading } = useSearchParcels({
    filters,
    sort,
    page,
    limit: perPage,
    market,
  });

  const parcels = data?.data ?? [];
  const totalPages = data?.meta?.totalPages ?? 0;

  return (
    <ParcelsPage
      parcels={parcels}
      pageCount={totalPages}
      isLoading={isLoading}
    />
  );
}
